import type { EventPayload } from '../types/destination'

/**
 * Display category for an event — used by devtools, extensions, and playgrounds.
 */
export type EventDisplayCategory = 'page' | 'track' | 'identify' | 'optin' | 'autotrack' | 'ads'

/**
 * Color palette for each display category (hex).
 */
export const EVENT_CATEGORY_COLORS: Record<EventDisplayCategory, string> = {
  page: '#3b82f6',      // blue
  track: '#a855f7',     // purple — custom track() events (kept clear of page's blue)
  optin: '#22c55e',     // green
  identify: '#14b8a6',  // teal
  autotrack: '#ec4899', // pink — delegated auto-capture (autoTrack)
  ads: '#f59e0b',       // amber — MontillaAds (impressão, clique, dispensa)
}

/**
 * Resolve the display category for an event.
 * page_view / page_exit → 'page' (page lifecycle)
 * identify → 'identify'
 * push opt-in → 'optin'
 * autoTrack-captured (carries `auto_track` marker) → 'autotrack'
 * MontillaAds (`ad_*`) → 'ads'
 * Otherwise (custom `track()` events) → 'track'
 */
export function resolveDisplayCategory(event: EventPayload): EventDisplayCategory {
  if (event.name === 'page_view' || event.name === 'page_exit') return 'page'
  if (event.name === 'identify') return 'identify'
  if (event.name.startsWith('push_') || event.name.startsWith('opt_in_')) return 'optin'
  // Antes do autoTrack por precaução: hoje `ad_*` não carrega o marcador de
  // captura delegada, mas um clique em anúncio TAMBÉM gera o evento do
  // autoTrack no link — e se um dia o marcador vier junto, um evento de ads
  // deve continuar sendo de ads.
  if (event.name.startsWith('ad_')) return 'ads'
  if ((event.attributes as Record<string, unknown> | undefined)?.auto_track === 'true') return 'autotrack'
  return 'track'
}

/**
 * Get the hex color for an event's display category.
 */
export function resolveDisplayColor(event: EventPayload): string {
  return EVENT_CATEGORY_COLORS[resolveDisplayCategory(event)]
}
